import Card from "@/components/ui/card";
import CardFallback from "@/components/ui/cardFallback";
import {
  useGetDashboardMetrics,
  useGetDashboardFormalityRequests,
} from "@/queries/dashboard/dashboard.api";
import { Icon } from "@elaniintc/goes-design-system";
import { useMetricsPermissions } from "./hooks/use-metrics-perms";

const Loading = () => {
  return (
    <Icon
      type="far"
      name="spinner-third"
      className="text-action-quiet animate-spin text-lg"
    />
  );
};

const Metrics = () => {
  const {
    data: metricsData,
    isLoading: isLoadingMetrics,
    isFetching: isFetchingMetrics,
    refetch: refetchMetrics,
  } = useGetDashboardMetrics();
  const {
    data: formalityRequestsData,
    isLoading: isLoadingFormalityRequests,
    isFetching: isFetchingFormalityRequests,
    refetch: refetchFormalityRequests,
  } = useGetDashboardFormalityRequests();
  const {
    canSeeMetricsRow,
    hasProcesses,
    canSeeFRRow,
    hasFlows,
    hasProcedures,
    hasInProgress,
    hasSolved,
    hasPending,
  } = useMetricsPermissions();

  const isLoadingMetricsSection = isLoadingMetrics || isFetchingMetrics;
  const isLoadingFormalityRequestsSection =
    isLoadingFormalityRequests || isFetchingFormalityRequests;

  return (
    <>
      {canSeeMetricsRow ? (
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <h6
              data-testid="metrics-title"
              className="text-heading text-h6 font-semibold"
            >
              Métricas
            </h6>
            <button
              onClick={() => refetchMetrics()}
              className="text-action text-sm font-semibold cursor-pointer hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              type="button"
              disabled={isFetchingMetrics}
            >
              Actualizar
            </button>
          </div>
          <div
            data-testid="metrics-cards"
            className="grid grid-cols-1 tablet:grid-cols-3 gap-3"
          >
            {hasProcesses ? (
              <Card
                data-testid="processes-card"
                className="flex justify-between items-center gap-4"
              >
                <span className="text-body font-normal">Procesos</span>
                {isLoadingMetricsSection ? (
                  <Loading />
                ) : (
                  <span className="text-heading font-semibold">
                    {metricsData?.data.metrics.processes_qty ?? "-"}
                  </span>
                )}
              </Card>
            ) : (
              <CardFallback
                data-testid="processes-card-fallback"
                className="flex justify-center items-center gap-4 shadow-none"
              >
                <span className="text-center font-normal text-on-disabled-quiet">
                  Sin permiso para ver estos datos
                </span>
              </CardFallback>
            )}
            {hasFlows ? (
              <Card
                data-testid="flows-card"
                className="flex justify-between items-center gap-4"
              >
                <span className="text-body font-normal">Flujos</span>
                {isLoadingMetricsSection ? (
                  <Loading />
                ) : (
                  <span className="text-heading font-semibold">
                    {metricsData?.data.metrics.flows_qty ?? "-"}
                  </span>
                )}
              </Card>
            ) : (
              <CardFallback
                data-testid="flows-card-fallback"
                className="flex justify-center items-center gap-4 text-center shadow-none"
              >
                <span className="text-center font-normal text-on-disabled-quiet">
                  Sin permiso para ver estos datos
                </span>
              </CardFallback>
            )}
            {hasProcedures ? (
              <Card
                data-testid="formalities-card"
                className="flex justify-between items-center gap-4"
              >
                <span className="text-body font-normal">Trámites</span>
                {isLoadingMetricsSection ? (
                  <Loading />
                ) : (
                  <span className="text-heading font-semibold">
                    {metricsData?.data.metrics.formality_requests_qty ?? "-"}
                  </span>
                )}
              </Card>
            ) : (
              <CardFallback
                data-testid="formalities-card-fallback"
                className="flex justify-center items-center gap-4 shadow-none"
              >
                <span className="text-center font-normal text-on-disabled-quiet">
                  Sin permiso para ver estos datos
                </span>
              </CardFallback>
            )}
          </div>
        </div>
      ) : null}
      {canSeeFRRow ? (
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <h6
              data-testid="requests-title"
              className="text-heading text-h6 font-semibold"
            >
              Trámites
            </h6>
            <button
              onClick={() => refetchFormalityRequests()}
              className="text-action text-sm font-semibold cursor-pointer hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              type="button"
              disabled={isFetchingFormalityRequests}
            >
              Actualizar
            </button>
          </div>
          <div
            data-testid="requests-cards"
            className="grid grid-cols-1 tablet:grid-cols-3 gap-3"
          >
            {hasInProgress ? (
              <Card
                data-testid="in-process-card"
                className="flex flex-col items-start gap-1"
              >
                <span className="text-body font-normal">
                  Solicitudes en proceso
                </span>
                <div className="flex items-center gap-2">
                  <Icon
                    type="far"
                    name="loader"
                    className="!text-action-quiet text-h5"
                  />
                  {isLoadingFormalityRequestsSection ? (
                    <Loading />
                  ) : (
                    <span className="text-heading text-h5 font-semibold">
                      {formalityRequestsData?.data.formality_requests
                        .in_process_qty ?? "-"}
                    </span>
                  )}
                </div>
              </Card>
            ) : (
              <CardFallback
                data-testid="in-process-card-fallback"
                className="flex justify-center items-center gap-4 shadow-none"
              >
                <span className="text-center font-normal text-on-disabled-quiet">
                  Sin permiso para ver estos datos
                </span>
              </CardFallback>
            )}
            {hasSolved ? (
              <Card
                data-testid="done-card"
                className="flex flex-col items-start gap-1"
              >
                <span className="text-body font-normal">
                  Solicitudes resueltas
                </span>
                <div className="flex items-center gap-2">
                  <Icon
                    type="far"
                    name="circle-check"
                    className="!text-success-quiet text-h5"
                  />
                  {isLoadingFormalityRequestsSection ? (
                    <Loading />
                  ) : (
                    <span className="text-heading text-h5 font-semibold">
                      {formalityRequestsData?.data.formality_requests
                        .done_qty ?? "-"}
                    </span>
                  )}
                </div>
              </Card>
            ) : (
              <CardFallback
                data-testid="done-card-fallback"
                className="flex justify-center items-center gap-4 shadow-none"
              >
                <span className="text-center font-normal text-on-disabled-quiet">
                  Sin permiso para ver estos datos
                </span>
              </CardFallback>
            )}
            {hasPending ? (
              <Card
                data-testid="pending-card"
                className="flex flex-col items-start gap-1"
              >
                <span className="text-body font-normal">
                  Solicitudes pendientes
                </span>
                <div className="flex items-center gap-2">
                  <Icon
                    type="far"
                    name="clock"
                    className="!text-warning-quiet text-h5"
                  />
                  {isLoadingFormalityRequestsSection ? (
                    <Loading />
                  ) : (
                    <span className="text-heading text-h5 font-semibold">
                      {formalityRequestsData?.data.formality_requests
                        .pending_qty ?? "-"}
                    </span>
                  )}
                </div>
              </Card>
            ) : (
              <CardFallback
                data-testid="pending-card-fallback"
                className="flex justify-center items-center gap-4 shadow-none"
              >
                <span className="text-center font-normal text-on-disabled-quiet">
                  Sin permiso para ver estos datos
                </span>
              </CardFallback>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default Metrics;
